import { useCallback, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { apiSend } from '../lib/api';
import { tourById, type Tour } from '../tours';

// Centralised state for the active tour. The Home page (or any page) calls
// `start(tour)` to begin; `TourEngine` listens via useTourState and renders
// the joyride overlay. On finish/skip we PATCH the user's preferences so
// the same tour isn't re-offered next session.

type TourState = {
  active: Tour | null;
  start: (tour: Tour) => void;
  finish: (tour: Tour) => Promise<void>;
  cancel: () => void;
};

// Module-scope subscriber pattern — keeps the API ergonomic (any component
// can call useTour) without pulling in a state library.
type Listener = (active: Tour | null) => void;
let activeTour: Tour | null = null;
const listeners = new Set<Listener>();

const setActive = (next: Tour | null) => {
  activeTour = next;
  listeners.forEach((l) => l(next));
};

export const useTour = (): TourState => {
  const { getToken, previewUserId } = useAuth();

  const start = useCallback((tour: Tour) => {
    setActive(tour);
  }, []);

  const finish = useCallback(
    async (tour: Tour) => {
      setActive(null);
      // Preview-mode admin shouldn't write to the target user's preferences.
      if (previewUserId) return;
      try {
        const token = await getToken();
        await apiSend(
          'PATCH',
          '/api/me/preferences',
          { toursSeen: { [tour.id]: tour.version } },
          token,
        );
      } catch {
        // Best-effort — local UX still works; the tour just gets re-offered
        // next session if the save failed.
      }
    },
    [getToken, previewUserId],
  );

  const cancel = useCallback(() => {
    setActive(null);
  }, []);

  return { active: activeTour, start, finish, cancel };
};

// Subscriber for the TourEngine to react to start/cancel from anywhere.
// Kept separate from useTour so the engine doesn't grab a stale closure.
export const useActiveTour = (): Tour | null => {
  const [active, setLocal] = useState<Tour | null>(activeTour);
  // Subscribe once on mount.
  useState(() => {
    const listener: Listener = (t) => setLocal(t);
    listeners.add(listener);
    return () => listeners.delete(listener);
  });
  return active;
};

// Convenience for replay-from-help.
export const startTourById = (id: string): void => {
  const t = tourById(id);
  if (t) setActive(t);
};
